import { useState, useEffect } from "react";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Syne:wght@400;600;700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #020408;
    --surface: #0a0f1a;
    --border: #1a2535;
    --accent: #00e5ff;
    --accent2: #ff3366;
    --text: #e8f0fe;
    --muted: #4a5568;
    --green: #00ff88;
  }

  body { background: var(--bg); color: var(--text); font-family: 'Syne', sans-serif; overflow-x: hidden; }

  .noise {
    position: fixed; inset: 0; pointer-events: none; z-index: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E");
    opacity: 0.4;
  }

  .grid-bg {
    position: fixed; inset: 0; pointer-events: none; z-index: 0;
    background-image: linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 0%, transparent 100%);
    opacity: 0.4;
  }

  .glow-orb {
    position: fixed; border-radius: 50%; pointer-events: none; z-index: 0; filter: blur(120px);
  }
  .orb1 { width: 600px; height: 600px; background: rgba(0, 229, 255, 0.06); top: -200px; right: -100px; }
  .orb2 { width: 500px; height: 500px; background: rgba(255, 51, 102, 0.05); bottom: 200px; left: -100px; }

  .container { max-width: 1100px; margin: 0 auto; padding: 0 32px; position: relative; z-index: 1; }

  /* NAV */
  nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
    border-bottom: 1px solid var(--border);
    backdrop-filter: blur(20px);
    background: rgba(2, 4, 8, 0.8);
    padding: 0 32px;
  }
  .nav-inner { max-width: 1100px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; height: 64px; }
  .logo { font-family: 'Space Mono', monospace; font-size: 18px; font-weight: 700; color: var(--accent); letter-spacing: -0.5px; }
  .logo span { color: var(--text); }
  .nav-links { display: flex; gap: 32px; align-items: center; }
  .nav-links a { color: var(--muted); text-decoration: none; font-size: 14px; font-weight: 600; letter-spacing: 0.5px; transition: color 0.2s; }
  .nav-links a:hover { color: var(--text); }
  .nav-cta { background: var(--accent); color: var(--bg) !important; padding: 8px 20px; border-radius: 4px; font-weight: 700 !important; }
  .nav-cta:hover { background: #00b8d4 !important; color: var(--bg) !important; }

  /* HERO */
  .hero { padding: 160px 0 100px; text-align: center; }
  .badge {
    display: inline-flex; align-items: center; gap: 8px;
    border: 1px solid var(--border); background: var(--surface);
    padding: 6px 16px; border-radius: 100px; font-family: 'Space Mono', monospace;
    font-size: 12px; color: var(--green); margin-bottom: 32px;
    animation: fadeUp 0.6s ease both;
  }
  .badge::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--green); animation: pulse 2s infinite; }
  @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

  h1 {
    font-size: clamp(48px, 7vw, 88px); font-weight: 800; line-height: 1;
    letter-spacing: -3px; margin-bottom: 24px;
    animation: fadeUp 0.6s 0.1s ease both;
  }
  h1 .accent { color: var(--accent); }
  h1 .accent2 { color: var(--accent2); }

  .hero-sub {
    font-size: 18px; color: var(--muted); max-width: 560px; margin: 0 auto 48px;
    line-height: 1.7; font-weight: 400;
    animation: fadeUp 0.6s 0.2s ease both;
  }

  .hero-cta { display: flex; gap: 16px; justify-content: center; animation: fadeUp 0.6s 0.3s ease both; }
  .btn-primary {
    background: var(--accent); color: var(--bg); padding: 14px 32px;
    border: none; border-radius: 4px; font-family: 'Syne', sans-serif;
    font-size: 15px; font-weight: 700; cursor: pointer; transition: all 0.2s;
    text-decoration: none; display: inline-block;
  }
  .btn-primary:hover { background: #00b8d4; transform: translateY(-2px); box-shadow: 0 8px 30px rgba(0,229,255,0.3); }
  .btn-secondary {
    background: transparent; color: var(--text); padding: 14px 32px;
    border: 1px solid var(--border); border-radius: 4px; font-family: 'Syne', sans-serif;
    font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s;
    text-decoration: none; display: inline-block;
  }
  .btn-secondary:hover { border-color: var(--accent); color: var(--accent); }

  /* CODE DEMO */
  .demo-section { padding: 80px 0; animation: fadeUp 0.6s 0.4s ease both; }
  .demo-card {
    background: var(--surface); border: 1px solid var(--border); border-radius: 12px;
    overflow: hidden; max-width: 800px; margin: 0 auto;
  }
  .demo-header { display: flex; align-items: center; gap: 8px; padding: 14px 20px; border-bottom: 1px solid var(--border); }
  .dot { width: 12px; height: 12px; border-radius: 50%; }
  .dot-r { background: #ff5f57; } .dot-y { background: #febc2e; } .dot-g { background: #28c840; }
  .demo-title { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--muted); margin-left: 8px; }
  .demo-body { display: grid; grid-template-columns: 1fr 1fr; }
  .demo-pane { padding: 24px; font-family: 'Space Mono', monospace; font-size: 13px; line-height: 1.8; }
  .demo-pane:first-child { border-right: 1px solid var(--border); }
  .demo-label { font-size: 10px; color: var(--muted); letter-spacing: 2px; margin-bottom: 16px; text-transform: uppercase; }
  .kw { color: var(--accent2); } .str { color: var(--green); } .num { color: var(--accent); } .key { color: #b39ddb; }

  /* STATS */
  .stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px; background: var(--border); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; margin: 80px 0; }
  .stat { background: var(--surface); padding: 40px 32px; text-align: center; }
  .stat-num { font-size: 48px; font-weight: 800; color: var(--accent); font-family: 'Space Mono', monospace; letter-spacing: -2px; }
  .stat-label { font-size: 13px; color: var(--muted); margin-top: 8px; font-weight: 600; letter-spacing: 0.5px; }

  /* FEATURES */
  .section-label { font-family: 'Space Mono', monospace; font-size: 11px; color: var(--accent); letter-spacing: 3px; text-transform: uppercase; margin-bottom: 16px; }
  .section-title { font-size: clamp(32px, 4vw, 48px); font-weight: 800; letter-spacing: -2px; margin-bottom: 60px; }

  .features { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px; background: var(--border); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; margin-bottom: 100px; }
  .feature { background: var(--surface); padding: 36px 32px; transition: background 0.2s; }
  .feature:hover { background: #0d1520; }
  .feature-icon { font-size: 28px; margin-bottom: 20px; }
  .feature-title { font-size: 17px; font-weight: 700; margin-bottom: 10px; }
  .feature-desc { font-size: 14px; color: var(--muted); line-height: 1.7; }

  /* PRICING */
  .pricing { padding: 80px 0 100px; }
  .pricing-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
  .plan {
    background: var(--surface); border: 1px solid var(--border); border-radius: 12px;
    padding: 36px 28px; position: relative; transition: transform 0.2s;
  }
  .plan:hover { transform: translateY(-4px); }
  .plan.featured { border-color: var(--accent); }
  .plan-badge { position: absolute; top: -12px; left: 50%; transform: translateX(-50%); background: var(--accent); color: var(--bg); font-size: 11px; font-weight: 700; padding: 4px 16px; border-radius: 100px; letter-spacing: 1px; white-space: nowrap; }
  .plan-name { font-size: 13px; font-weight: 700; color: var(--muted); letter-spacing: 2px; text-transform: uppercase; margin-bottom: 16px; }
  .plan-price { font-family: 'Space Mono', monospace; font-size: 48px; font-weight: 700; letter-spacing: -2px; margin-bottom: 4px; }
  .plan-price span { font-size: 20px; color: var(--muted); font-weight: 400; }
  .plan-calls { font-size: 13px; color: var(--muted); margin-bottom: 32px; }
  .plan-features { list-style: none; margin-bottom: 32px; }
  .plan-features li { font-size: 14px; padding: 8px 0; border-bottom: 1px solid var(--border); color: var(--muted); display: flex; align-items: center; gap: 10px; }
  .plan-features li::before { content: '✓'; color: var(--green); font-weight: 700; font-size: 12px; }
  .plan-btn { width: 100%; padding: 12px; border-radius: 4px; font-family: 'Syne', sans-serif; font-size: 14px; font-weight: 700; cursor: pointer; transition: all 0.2s; }
  .plan-btn-outline { background: transparent; color: var(--text); border: 1px solid var(--border); }
  .plan-btn-outline:hover { border-color: var(--accent); color: var(--accent); }
  .plan-btn-filled { background: var(--accent); color: var(--bg); border: none; }
  .plan-btn-filled:hover { background: #00b8d4; }

  /* DEMO TESTER */
  .tester { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 48px; margin: 80px 0; }
  .tester-title { font-size: 24px; font-weight: 800; margin-bottom: 8px; letter-spacing: -1px; }
  .tester-sub { color: var(--muted); font-size: 14px; margin-bottom: 32px; }
  .tester-form { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 24px; }
  .input-group label { display: block; font-size: 11px; color: var(--muted); letter-spacing: 1px; text-transform: uppercase; margin-bottom: 8px; font-weight: 600; }
  .input-group input, .input-group select {
    width: 100%; background: var(--bg); border: 1px solid var(--border); border-radius: 4px;
    padding: 10px 14px; color: var(--text); font-family: 'Space Mono', monospace; font-size: 13px;
    outline: none; transition: border-color 0.2s;
  }
  .input-group input:focus, .input-group select:focus { border-color: var(--accent); }
  .result-box { background: var(--bg); border: 1px solid var(--border); border-radius: 8px; padding: 20px; font-family: 'Space Mono', monospace; font-size: 13px; line-height: 1.8; min-height: 120px; }
  .result-fraud { border-color: var(--accent2); }
  .result-safe { border-color: var(--green); }

  /* FOOTER */
  footer { border-top: 1px solid var(--border); padding: 40px 0; text-align: center; }
  .footer-logo { font-family: 'Space Mono', monospace; font-size: 20px; color: var(--accent); margin-bottom: 12px; font-weight: 700; }
  .footer-sub { color: var(--muted); font-size: 13px; }

  @media (max-width: 768px) {
    .demo-body, .stats, .features, .pricing-grid, .tester-form { grid-template-columns: 1fr; }
    .nav-links { display: none; }
  }
`;

const API_BASE = "https://fraudguardapi.xyz/api";

export default function App() {
  const [form, setForm] = useState({ transaction_id: "TXN-001", amount: "5000", sender_id: "user_123", receiver_id: "user_456", transaction_type: "transfer" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);

  useEffect(() => {
    const target = 97;
    const timer = setInterval(() => { setCount(c => { if (c >= target) { clearInterval(timer); return c; } return c + 1; }); }, 20);
    return () => clearInterval(timer);
  }, []);

  const testAPI = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API_BASE}/v1/predict`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-api-key": "test-key-123" },
        body: JSON.stringify({ ...form, amount: parseFloat(form.amount) })
      });
      const data = await res.json();
      setResult(data);
    } catch {
      setResult({ error: "Could not connect to API. Make sure the server is running." });
    }
    setLoading(false);
  };

  return (
    <>
      <style>{styles}</style>
      <div className="noise" />
      <div className="grid-bg" />
      <div className="glow-orb orb1" />
      <div className="glow-orb orb2" />

      <nav>
        <div className="nav-inner">
          <div className="logo">Fraud<span>Guard</span></div>
          <div className="nav-links">
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <a href={`${API_BASE}/docs`} target="_blank" rel="noreferrer">API Docs</a>
            <a href="#try" className="nav-cta">Get Started</a>
          </div>
        </div>
      </nav>

      <div className="container">
        <section className="hero">
          <div className="badge">LIVE ON AWS · MUMBAI REGION</div>
          <h1>Fraud Detection<br />as an <span className="accent">API</span></h1>
          <p className="hero-sub">GraphSAGE-powered fraud intelligence for fintech, e-commerce, and payment platforms. Integrate in minutes. Stop fraud in milliseconds.</p>
          <div className="hero-cta">
            <a href="#try" className="btn-primary">Try Free API</a>
            <a href={`${API_BASE}/docs`} target="_blank" rel="noreferrer" className="btn-secondary">View Docs →</a>
          </div>
        </section>

        <div className="demo-section">
          <div className="demo-card">
            <div className="demo-header">
              <div className="dot dot-r"/><div className="dot dot-y"/><div className="dot dot-g"/>
              <span className="demo-title">POST /v1/predict</span>
            </div>
            <div className="demo-body">
              <div className="demo-pane">
                <div className="demo-label">Request</div>
                <div><span className="kw">POST</span> /v1/predict</div>
                <div><span className="key">x-api-key:</span> <span className="str">fg_live_***</span></div>
                <br/>
                <div>{"{"}</div>
                <div>&nbsp;&nbsp;<span className="key">"transaction_id"</span>: <span className="str">"TXN-8821"</span>,</div>
                <div>&nbsp;&nbsp;<span className="key">"amount"</span>: <span className="num">15000</span>,</div>
                <div>&nbsp;&nbsp;<span className="key">"sender_id"</span>: <span className="str">"usr_44a"</span>,</div>
                <div>&nbsp;&nbsp;<span className="key">"receiver_id"</span>: <span className="str">"usr_99x"</span></div>
                <div>{"}"}</div>
              </div>
              <div className="demo-pane">
                <div className="demo-label">Response · 42ms</div>
                <div>{"{"}</div>
                <div>&nbsp;&nbsp;<span className="key">"is_fraud"</span>: <span className="kw">true</span>,</div>
                <div>&nbsp;&nbsp;<span className="key">"fraud_probability"</span>: <span className="num">0.9341</span>,</div>
                <div>&nbsp;&nbsp;<span className="key">"risk_level"</span>: <span className="str">"HIGH"</span>,</div>
                <div>&nbsp;&nbsp;<span className="key">"message"</span>: <span className="str">"HIGH risk"</span></div>
                <div>{"}"}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="stats">
          <div className="stat"><div className="stat-num">{count}%</div><div className="stat-label">AUC Score</div></div>
          <div className="stat"><div className="stat-num">&lt;50ms</div><div className="stat-label">Avg Response Time</div></div>
          <div className="stat"><div className="stat-num">24/7</div><div className="stat-label">Uptime SLA</div></div>
        </div>

        <section id="features">
          <div className="section-label">Why FraudGuard</div>
          <div className="section-title">Built on Graph Intelligence</div>
          <div className="features">
            {[
              { icon: "🕸️", title: "GraphSAGE Model", desc: "Detects complex fraud rings by analyzing transaction network topology — not just individual transactions." },
              { icon: "⚡", title: "Real-Time API", desc: "Sub-50ms inference via FastAPI on AWS EC2. No batch processing delays. Block fraud at checkout." },
              { icon: "🔑", title: "Simple Integration", desc: "One POST request. No SDKs required. Works with any language, any framework, any stack." },
              { icon: "🛡️", title: "Risk Levels", desc: "Returns LOW / MEDIUM / HIGH risk classification with fraud probability score for every transaction." },
              { icon: "📊", title: "97.6% AUC", desc: "Trained on realistic transaction graphs with GraphSAGE — outperforms traditional ML approaches." },
              { icon: "🌍", title: "Global Ready", desc: "Hosted on AWS Mumbai. API-key authenticated. Designed for SEA, LatAm, and US/EU markets." },
            ].map((f, i) => (
              <div className="feature" key={i}>
                <div className="feature-icon">{f.icon}</div>
                <div className="feature-title">{f.title}</div>
                <div className="feature-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </section>

        <section id="try">
          <div className="tester">
            <div className="tester-title">Try the API Live</div>
            <div className="tester-sub">Send a real request to the FraudGuard API running on AWS EC2.</div>
            <div className="tester-form">
              {[
                { label: "Transaction ID", key: "transaction_id" },
                { label: "Amount (₹)", key: "amount" },
                { label: "Sender ID", key: "sender_id" },
                { label: "Receiver ID", key: "receiver_id" },
              ].map(f => (
                <div className="input-group" key={f.key}>
                  <label>{f.label}</label>
                  <input value={form[f.key]} onChange={e => setForm({...form, [f.key]: e.target.value})} />
                </div>
              ))}
            </div>
            <button className="btn-primary" onClick={testAPI} disabled={loading} style={{marginBottom: 24}}>
              {loading ? "Analyzing..." : "→ Run Fraud Check"}
            </button>
            {result && (
              <div className={`result-box ${result.error ? "" : result.is_fraud ? "result-fraud" : "result-safe"}`}>
                {result.error ? (
                  <span style={{color: "var(--muted)"}}>{result.error}</span>
                ) : (
                  <>
                    <div><span className="key">is_fraud:</span> <span className={result.is_fraud ? "kw" : "str"}>{String(result.is_fraud)}</span></div>
                    <div><span className="key">fraud_probability:</span> <span className="num">{result.fraud_probability}</span></div>
                    <div><span className="key">risk_level:</span> <span className="str">"{result.risk_level}"</span></div>
                    <div><span className="key">message:</span> <span className="str">"{result.message}"</span></div>
                  </>
                )}
              </div>
            )}
          </div>
        </section>

        <section id="pricing" className="pricing">
          <div className="section-label">Pricing</div>
          <div className="section-title">Simple, Usage-Based</div>
          <div className="pricing-grid">
            {[
              { name: "Free", price: "$0", calls: "100 calls/month", features: ["GraphSAGE inference", "REST API access", "Swagger docs", "Email support"], btn: "btn-outline", label: "Start Free" },
              { name: "Starter", price: "$29", calls: "10,000 calls/month", features: ["Everything in Free", "Priority inference", "API key management", "SLA guarantee"], btn: "btn-filled", label: "Get Started", featured: true, badge: "MOST POPULAR" },
              { name: "Scale", price: "$99", calls: "100,000 calls/month", features: ["Everything in Starter", "Dedicated support", "Custom model tuning", "Invoice billing"], btn: "btn-outline", label: "Contact Us" },
            ].map((p, i) => (
              <div className={`plan ${p.featured ? "featured" : ""}`} key={i}>
                {p.badge && <div className="plan-badge">{p.badge}</div>}
                <div className="plan-name">{p.name}</div>
                <div className="plan-price">{p.price}<span>/mo</span></div>
                <div className="plan-calls">{p.calls}</div>
                <ul className="plan-features">{p.features.map((f, j) => <li key={j}>{f}</li>)}</ul>
                <button className={`plan-btn plan-${p.btn}`}>{p.label}</button>
              </div>
            ))}
          </div>
        </section>
      </div>

      <footer>
        <div className="footer-logo">FraudGuard</div>
        <div className="footer-sub">Built by Spandan Das · Powered by GraphSAGE + AWS EC2</div>
      </footer>
    </>
  );
}